/**
 * API Endpoints Configuration - Process Design Module
 *
 * This file defines API endpoints for Process Design features only.
 * It serves as a contract between frontend and backend teams.
 */

export const API_ENDPOINTS = {
  // Process Extraction & Management
  extractProcesses: '/api/extract-processes', // ✅ IMPLEMENTED
  getDraftProcesses: (cycleId) => `/api/cycles/${cycleId}/draft-processes`, // TODO: Implement
  finalizeProcesses: (cycleId) => `/api/sox-cycles/${cycleId}/processes`, // TODO: Implement
  finalizeAiAssistedProcesses: (cycleId) => `/api/sox-cycles/${cycleId}/processes/ai_assisted`,
  /** List processes for a cycle — use business `cycle_id` in the path. */
  getProcesses: (cycleId) => `/api/sox-cycles/${cycleId}/processes`, // ✅ IMPLEMENTED
  /** Reorder process sequence for a cycle — body: { process_ids: process UUID[] }. */
  reorderCycleProcessesSequence: (cycleId) => `/api/sox-cycles/${cycleId}/processes/sequence`,
  /** Sync create/update/delete process list for a cycle (PATCH). */
  syncProcessesByCycle: (cycleId) => `/api/processes/cycle/${cycleId}`,
  /** Create a single process with body including cycleId. */
  createProcess: '/api/processes',
  /** Single process fetch/patch by process_id. */
  getProcessById: (processId) => `/api/processes/${processId}`,
  /** Process details scoped with cycle metadata (GET /api/processes/{process_id}). */
  getProcessByIdWithCycle: (processId) => `/api/processes/${processId}`,
  patchProcess: (processId) => `/api/processes/${processId}`,

  // Flowchart Operations
  generateFlowchart: '/api/generateFlowchart',
  getFlowchart: (cycleId, processId) => `/api/flowchart/${cycleId}/${processId}`, // ✅ IMPLEMENTED
  getFlowchartVersions: (cycleId, processId) => `/api/flowchart/${cycleId}/${processId}/versions`,
  saveFlowchart: '/api/update-flowchart', // ✅ IMPLEMENTED
  updateFlowchartNodePositions: (flowchartVersionId) =>
    `/api/flowchart/${flowchartVersionId}/update-node-positions`,
  publishFlowchartVersion: (flowchartVersionId) => `/api/flowchart/${flowchartVersionId}/publish`,
  setActiveFlowchartVersion: (flowchartVersionId) => `/api/flowchart/${flowchartVersionId}/set-active`,
  updateFlowchartConnectorLayout: (flowchartVersionId) =>
    `/api/flowchart/${flowchartVersionId}/connector-layout`,
  updateFlowchartSwimlaneLayout: (flowchartVersionId) =>
    `/api/flowchart/${flowchartVersionId}/swimlane-layout`,
  getUnmappedFlowchartRisks: (processUuid, flowchartVersionId) =>
    `/api/unmapped-risks/process/${encodeURIComponent(processUuid)}/flowchart-version/${encodeURIComponent(flowchartVersionId)}`,
  attachManualRiskToFlowchart: '/api/unmapped-risks/attach',
  detachManualRiskFromFlowchart: '/api/unmapped-risks/detach',
  getFlowchartSubprocessLinks: (flowchartVersionId) =>
    `/api/flowchart/subprocess-links?flowchart_version_id=${encodeURIComponent(flowchartVersionId)}`,
  getIncomingFlowchartSubprocessLinks: (processUuid) =>
    `/api/flowchart/subprocess-links?target_process_id=${encodeURIComponent(processUuid)}`,
  createFlowchartSubprocessLinks: '/api/flowchart/subprocess-links',
  deleteFlowchartSubprocessLink: (linkId) =>
    `/api/flowchart/subprocess-links/${encodeURIComponent(linkId)}`,
  getSubprocessNodeCatalog: (cycleId) =>
    `/api/sox-cycles/${encodeURIComponent(cycleId)}/subprocess-node-catalog`,

  // SOX Cycles
  getSoxCycles: '/api/getUniqueSOXCycles', // ✅ IMPLEMENTED (legacy endpoint)
  /** Feature 1.2: list all cycles including locked ones (GET /api/sox-cycles). */
  listSoxCycles: '/api/sox-cycles',
  /** Default cycle codes + names + line-item mappings for materiality / dashboard pickers */
  getSoxCycleMappingsDefaults: '/api/sox-cycle-mappings/defaults',
  createSoxCycle: '/api/sox-cycles', // ✅ IMPLEMENTED
  /** Feature 1.2: carry-forward clone + lock source cycle. */
  carryForwardSoxCycle: (cycleUuid) => `/api/sox-cycles/${cycleUuid}/carry-forward`,

  // Fiscal Years (Feature 1.2)
  listFiscalYears: '/api/fiscal-years',
  createFiscalYear: '/api/fiscal-years',
  getFiscalYear: (id) => `/api/fiscal-years/${id}`,
  updateFiscalYear: (id) => `/api/fiscal-years/${id}`,
  deleteFiscalYear: (id) => `/api/fiscal-years/${id}`,
  deleteSoxCycle: (cycleUuid) => `/api/sox-cycles/${cycleUuid}`,
  /** PUT body: { status: string } — path uses cycle UUID */
  putSoxCycleStatus: (cycleUuid) => `/api/sox-cycles/${cycleUuid}/status`,
  /** PUT body: { riskScore: number } — path uses cycle UUID */
  putSoxCycleRiskScore: (cycleUuid) => `/api/sox-cycles/${cycleUuid}/risk-score`,
  /** PATCH body: { is_active: boolean } — path uses cycle UUID */
  patchSoxCycleToggleActive: (cycleUuid) =>
    `/api/sox-cycles/${cycleUuid}/toggle-active-inactive-status`,
  /** PATCH — path uses cycle UUID */
  patchSoxCycleArchive: (cycleUuid) => `/api/sox-cycles/${cycleUuid}/archive`,
  patchSoxCycleUnarchive: (cycleUuid) => `/api/sox-cycles/${cycleUuid}/unarchive`,

  /** PUT body: { status: string } — path uses process UUID */
  putProcessStatus: (processUuid) => `/api/processes/${processUuid}/status`,
  /** PATCH body: { is_active: boolean } — path uses process UUID */
  patchProcessToggleActive: (processUuid) =>
    `/api/processes/${processUuid}/toggle-active-inactive-status`,
  /** DELETE — path uses process UUID */
  deleteProcess: (processUuid) => `/api/processes/${processUuid}`,
  /** PATCH body: editable process fields — path uses process UUID/process_id */
  updateProcess: (processUuid) => `/api/processes/${processUuid}`,

  /** GET binary — `name` is `process` or `risk` (backend sample template keys). */
  getSampleExcelTemplate: (name) => `/api/sample-excel/${encodeURIComponent(name)}`,

  /** Cycle end-to-end process map (positions + connectors). */
  getCycleEndToEndMap: (cycleId) => `/api/sox-cycles/${cycleId}/end-to-end-map`,
  saveCycleEndToEndMap: (cycleId) => `/api/sox-cycles/${cycleId}/end-to-end-map`,
};
